import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ScoreboardCellsProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import {
  ESportsPeriodType,
  ESportsScoreType, type ScoresPartOfGameEntity,
} from 'app/lib/betting/betting-entities';
import { TEAMS } from 'app/lib/betting/constants';

import MainScoreWrapper from '../MainScoreWrapper';
import { ScoreboardCell, ScoreboardCellWrapper } from '../ScoreboardCell';

import dictionary from './CounterStrike.dictionary.json';

import styles from '../../ScoreboardCells.module.scss';

type StoreProps = {
  mapScores: ScoresPartOfGameEntity | null;
  isMapFinished: boolean;
  mapPeriodNumber: number;
};

const {
  POINT,
} = ESportsScoreType;
const {
  MATCH,
  MAP,
} = ESportsPeriodType;

const CounterScoreboardCellsWithoutHOC: FC<ScoreboardCellsProps & StoreProps> = ({
  mapScores,
  isMapFinished,
  mapPeriodNumber,
  matchId,
}) => {
  const { t } = useTranslation();
  const cells = [];

  if (mapScores !== null) {
    mapScores.forEach((scores, mapNumber) => {
      const currentPeriod = mapNumber === mapPeriodNumber && !isMapFinished;
      cells.push(
        <ScoreboardCellWrapper
          content={t(dictionary.map, { mapNumber })}
          className={classNames({ [styles.currentPeriodScore]: currentPeriod })}
          key={mapNumber}>
          {TEAMS.map((teamId) => (
            <ScoreboardCell key={teamId}>
              {scores.get(teamId, '–')}
            </ScoreboardCell>
          ))}
        </ScoreboardCellWrapper>,
      );
    });
  }

  cells.push(<MainScoreWrapper matchId={matchId}
                               key="ms" />);

  return <>{cells}</>;
};

const mapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      content: {
        selectors: { matches },
      },
    },
    matchId,
  }: BettingContextProps,
): StoreProps => {
  const match = matches(state).get(matchId);
  const scores = match?.scores;
  const periods = match?.periods;

  return {
    mapScores: scores?.getIn([MATCH, POINT], null),
    isMapFinished: periods?.get(MAP)?.finished,
    mapPeriodNumber: periods?.get(MAP)?.number,
  };
};

export const CounterScoreboardCells = compose<FC<ScoreboardCellsProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(CounterScoreboardCellsWithoutHOC);
